import { Injectable } from '@angular/core';
import {
  Router,
  UrlTree,
  ActivatedRouteSnapshot,
  CanActivateChild,
  CanActivate,
  RouterStateSnapshot
} from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';

import { User } from '@app/models/user-data';
import { Routes } from '@app/constants/routes.constants';
import { Role } from '@app/models/role.enum';
import { MessageService } from '@app/services/message.service';
import { ErrorMessage } from '@app/constants/messages.constants';
import { CORRECT_MONTH_NUMBER } from '@app/shared/constants/timesheet.constants';

import { AuthService } from '../services/auth.service';
import { AuthDOS } from '../services/auth-dos.service';
import { LocalStorageItem } from '../auth.constants';
import { TokenService } from '../services/token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _authDOS: AuthDOS,
    private _tokenService: TokenService,
    private _messageService: MessageService
  ) {
    this.checkUserAtLogin();
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this._authDOS.user$.pipe(
      switchMap((user) => {
        return user ? of(user) : this.loadAndNavigateUser(route);
      }),
      map((user) => {
        if (!this._tokenService.isRefreshTokenPresent()) {
          this._authService.signOut();
          return false;
        }

        if (!this._authDOS.isAuthenticated()) {
          this._authService.signIn();
          return false;
        }

        if (!this._authService.isAuthorized(route.data?.allowedRoles)) {
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth() + CORRECT_MONTH_NUMBER;
          return this._router.createUrlTree([Routes.Timesheet, currentYear, currentMonth]);
        }

        // TODO find a way to keep login and redirect on refresh flow without using localStorage
        if (!localStorage.getItem(LocalStorageItem.IsLogged) || state.url === Routes.Auth) {
          return this.navigateToStartPage(user.userRole);
        }

        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        if (!this._tokenService.isRefreshTokenPresent()) {
          this._authService.signOut();
          return of(false);
        }

        if (state.url === Routes.Auth) {
          return of(true);
        }

        this._router.navigate([Routes.Auth], { skipLocationChange: true });
        return of(false);
      })
    );
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.canActivate(route, state);
  }

  private checkUserAtLogin(): void {
    if (this._tokenService.isRefreshTokenPresent()) {
      this._authDOS.setAuthTokenTrue();
    } else {
      this._authDOS.setAuthTokenFalse();
      this._authService.signIn();
    }
  }

  private loadAndNavigateUser(route: ActivatedRouteSnapshot): Observable<User> {
    return this._authService.getUserData().pipe(
      switchMap((user) => {
        if (user.userRole === Role.Admin) {
          return this._authService.updateCache().pipe(map(() => user));
        } else {
          return of(user);
        }
      }),
      tap((user) => {
        if (user) {
          this._authDOS.updateUser(user);
          localStorage.setItem(LocalStorageItem.IsLogged, 'true');
        }
      }),
      catchError((err) => {
        if (route.toString().includes(Routes.Auth.replace('/', ''))) {
          this._messageService.showErrorMessage(ErrorMessage.GetUserData);
        }
        return throwError(err);
      })
    );
  }

  private navigateToStartPage(userRole: Role): boolean | UrlTree {
    switch (userRole) {
      case Role.Admin:
        return this._router.createUrlTree([Routes.Timesheets]);
      case Role.User:
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + CORRECT_MONTH_NUMBER;
        return this._router.createUrlTree([Routes.Timesheet, currentYear, currentMonth]);
      default:
        return false;
    }
  }
}
